import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MHeader from "@/components/ui/organisms/MHeader";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import NavigationButton from "@/components/ui/atoms/MNavigationButton";
import { getMemberInfo } from "@/hooks/useMembers";
import { useDocument } from "@/hooks/useDocument";
import { FileCategory } from "@/types/document.dto";
import {
  IconPrescription,
  IconFirstAidKit,
  IconClipboardText,
  IconCoinRupee,
  IconUser,
  IconShieldHeart,
  IconChevronRight,
  IconBubbleText,
} from "@tabler/icons-react";
import MDocumentUploadOptions from "@/components/ui/molecules/MDocumentUploadOptions";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const DocumentsNavigation: React.FC = () => {
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const navigate = useNavigate();
  const location = useLocation();
  const { getNavigationSource, hasNavigationSource } = useNavigationSource();

  // Clear stored years when navigation page loads
  React.useEffect(() => {
    localStorage.removeItem("documentListingAvailableYears");
  }, []);

  const handleBack = () => {
    // Check for backUrl in location state first (from Dashboard or other sources)
    const backUrl = (location.state as any)?.backUrl;
    if (backUrl) {
      navigate(backUrl);
      return;
    }

    // Fall back to navigation source hook
    if (hasNavigationSource()) {
      const source = getNavigationSource();
      if (source?.path) {
        navigate(source.path);
        return;
      }
    }

    // Final fallback to documents page
    navigate("/documents");
  };

  const { getDocumentTypeCounts } = useDocument(memberId);

  type DocumentTypeCounts = {
    prescriptions?: number;
    labReports?: number;
    dischargeSummaries?: number;
    medicalReceipts?: number;
    insurances?: number;
    identity?: number;
    healthMemories?: number;
    privateDocsCount?: number;
  };

  const [typeCounts, setTypeCounts] = React.useState<DocumentTypeCounts | null>(
    null
  );
  const [showUploadOptions, setShowUploadOptions] = React.useState(false);
  const uploadOptionsRef = React.useRef(null);

  // Localized search focus viewport fix for DocumentsNavigation only
  React.useEffect(() => {
    const mainElement = document.querySelector("main");
    if (!mainElement) return;

    const fixViewportHeight = () => {
      const isSearchFocused =
        document.body.classList.contains("search-focused");
      if (isSearchFocused) {
        mainElement.style.maxHeight = "none";
        mainElement.style.height = "auto";
      } else {
        mainElement.style.maxHeight = "";
        mainElement.style.height = "";
      }
    };

    const observer = new MutationObserver(fixViewportHeight);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Check immediately
    fixViewportHeight();

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchCounts() {
      try {
        // Fetch full type counts including health memories
        const raw = await getDocumentTypeCounts();
        if (!cancelled) {
          setTypeCounts(raw as DocumentTypeCounts);
        }
      } catch (e) {
        // swallow for now; page can still render with zeros
      }
    }
    fetchCounts();
    return () => {
      cancelled = true;
    };
    // Run once per memberId; avoid depending on getDocumentTypeCounts function ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const totalDocuments = React.useMemo(() => {
    if (!typeCounts) return 0;
    const {
      prescriptions = 0,
      labReports = 0,
      dischargeSummaries = 0,
      medicalReceipts = 0,
      insurances = 0,
      identity = 0,
      healthMemories = 0,
      privateDocsCount = 0,
    } = typeCounts;
    // Exclude private docs when viewing another member's documents — they
    // shouldn't see the private count in "See all N records". Self keeps
    // the private docs in their own total.
    const privateContribution = memberInfo.onBehalf ? 0 : privateDocsCount;
    return (
      prescriptions +
      labReports +
      dischargeSummaries +
      medicalReceipts +
      insurances +
      identity +
      healthMemories +
      privateContribution
    );
  }, [typeCounts, memberInfo.onBehalf]);

  // Track page load with document counts
  React.useEffect(() => {
    if (typeCounts !== null) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_HOME,
        additionalUserProperties: {
          total_document_count: String(totalDocuments),
          prescriptions: String(typeCounts.prescriptions ?? 0),
          procedures: String(typeCounts.dischargeSummaries ?? 0),
          reports: String(typeCounts.labReports ?? 0),
          bills: String(typeCounts.medicalReceipts ?? 0),
          identity: String(typeCounts.identity ?? 0),
          insurance: String(typeCounts.insurances ?? 0),
          health_memories: String(typeCounts.healthMemories ?? 0),
        },
      });
    }
  }, [typeCounts, totalDocuments]);

  function handleNavigateWithCategory(category: FileCategory) {
    // Clear any leftover search/year filter from a prior visit so it doesn't
    // bleed across profile switches or category clicks (DocumentListing
    // restores `documentFilters` from localStorage when the URL lacks the
    // params, which silently filters the list to zero results).
    localStorage.removeItem("documentFilters");
    navigate(`/documents/list?category=${category}`, {
      state: { backUrl: "/documents/navigation" },
    });
  }

  return (
    <div style={{ padding: "0 24px" }}>
      <MHeader
        subHeading="All documents"
        module="documents"
        memberInfo={memberInfo}
        showProfilePickerRow
        showSearch={true}
        searchPlaceholder="Try: Blood pressure, Dr Jain, Crocin..."
        showGradient={true}
        onSearch={(val) => {
          if (!val) return;
          if (val.length >= 3) {
            localStorage.removeItem("documentFilters");
            navigate(`/documents/list?search=${encodeURIComponent(val)}`);
          }
        }}
        onAdd={() => setShowUploadOptions(true)}
        onBack={handleBack}
      />

      {/* Main content area with white background */}
      <div
        style={{
          background: "#FFF",
          marginLeft: "-24px",
          marginRight: "-24px",
          padding: "0 24px",
          paddingBottom: "100px",
        }}
      >
        {/* See all documents row */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <button
            type="button"
            style={{
              border: "none",
              background: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginTop: 24,
            }}
            onClick={() => {
              localStorage.removeItem("documentFilters");
              navigate("/documents/list", {
                state: { backUrl: "/documents/navigation" },
              });
            }}
          >
            <span className="body-s-semibold" style={{ color: "#B23F83" }}>
              {`See all ${totalDocuments} records`}
            </span>
            <IconChevronRight size={20} style={{ color: "#B23F83" }} />
          </button>
        </div>

        {/* Category navigation list */}
        <div style={{ marginTop: 16 }}>
          <NavigationButton
            variant="docs"
            leftIcon={<IconPrescription size={20} />}
            rightIcon={<IconChevronRight size={20} />}
            text={`Prescriptions (${typeCounts?.prescriptions ?? 0})`}
            onClick={() =>
              handleNavigateWithCategory(FileCategory.Prescription)
            }
          />
          <NavigationButton
            variant="docs"
            leftIcon={<IconFirstAidKit size={20} />}
            rightIcon={<IconChevronRight size={20} />}
            text={`Procedures (${typeCounts?.dischargeSummaries ?? 0})`}
            onClick={() =>
              handleNavigateWithCategory(FileCategory.DischargeSummary)
            }
          />
          <NavigationButton
            variant="docs"
            leftIcon={<IconClipboardText size={20} />}
            rightIcon={<IconChevronRight size={20} />}
            text={`Reports (${typeCounts?.labReports ?? 0})`}
            onClick={() => handleNavigateWithCategory(FileCategory.LabReport)}
          />
          <NavigationButton
            variant="docs"
            leftIcon={<IconCoinRupee size={20} />}
            rightIcon={<IconChevronRight size={20} />}
            text={`Bills (${typeCounts?.medicalReceipts ?? 0})`}
            onClick={() => handleNavigateWithCategory(FileCategory.MedicalBill)}
          />
          <NavigationButton
            variant="docs"
            leftIcon={<IconUser size={20} />}
            rightIcon={<IconChevronRight size={20} />}
            text={`Identity (${typeCounts?.identity ?? 0})`}
            onClick={() => handleNavigateWithCategory(FileCategory.Identity)}
          />
          <NavigationButton
            variant="docs"
            leftIcon={<IconShieldHeart size={20} />}
            rightIcon={<IconChevronRight size={20} />}
            text={`Insurance (${typeCounts?.insurances ?? 0})`}
            onClick={() =>
              handleNavigateWithCategory(FileCategory.InsuranceCard)
            }
          />
          <NavigationButton
            variant="docs"
            leftIcon={<IconBubbleText size={20} />}
            rightIcon={<IconChevronRight size={20} />}
            text={`Health memories (${typeCounts?.healthMemories ?? 0})`}
            onClick={() =>
              navigate("/documents/health-memory", {
                state: { backUrl: "/documents/navigation" },
              })
            }
          />
          {(typeCounts?.privateDocsCount ?? 0) > 0 && (
            <NavigationButton
              variant="docs"
              leftIcon={<IconShieldHeart size={20} />}
              text={`Private documents (${typeCounts?.privateDocsCount ?? 0})`}
              onClick={() => {}}
            />
          )}
        </div>
      </div>

      <MDocumentUploadOptions
        ref={uploadOptionsRef}
        open={showUploadOptions}
        onClose={() => setShowUploadOptions(false)}
      />
    </div>
  );
};

export default DocumentsNavigation;
